import type { AHProduct } from '@/services/ah-service'

// Albert Heijn's search is tuned for shoppers browsing, not for matching a bare
// ingredient name: "uien" returns a crisps multipack before the actual onions.
// These weights re-rank the candidates so the obvious product wins.
const SCORE_EXACT = 3
/** "ui" and "uien" are the same product. */
const SCORE_SAME_STEM = 2.8
/** Dutch compounds are head-final: "roomboter" IS a boter. */
const SCORE_COMPOUND_HEAD = 2
/** ...but "boterhamworst" is a worst, not a boter. */
const SCORE_COMPOUND_PREFIX = -1
const SCORE_ELSEWHERE = -0.5
const SCORE_MISSING = -5
/**
 * Weighted so a plain product beats a long ready-made one that merely mentions
 * the ingredient: "AH Kristalsuiker" over "AH Siroop peer 0% suiker toegevoegd".
 */
const PENALTY_EXTRA_TOKEN = 0.45
const PENALTY_MULTIPACK = 1
const PENALTY_UNAVAILABLE = 5
/** A plain "roomboter" beats a "knoflookboter" for the ingredient "boter". */
const PENALTY_COMPOUND_LENGTH = 10
const PENALTY_NON_FOOD = 3
const PRICE_TIEBREAK = 0.001

/** Searching an ingredient can surface cleaning products and kitchen tools. */
const NON_FOOD_TOKENS = new Set([
  'schoonmaak',
  'schoonmaakazijn',
  'allesreiniger',
  'wasmiddel',
  'vaatwas',
  'afwasmiddel',
  'shampoo',
  'servet',
  'servetten',
  'lepel',
  'vork',
  'spatel',
  'pollepel',
  'luiers',
  'toiletpapier',
])

/**
 * Words that describe a variant of the same product rather than a different
 * one. "AH Roomboter ongezouten" is still boter; "AH Siroop peer met suiker"
 * is not suiker. Only the latter kind of extra word counts as noise.
 */
const QUALIFIER_TOKENS = new Set([
  'ah', 'bio', 'biologisch', 'biologische', 'terra', 'excellent', 'basic',
  'vers', 'verse', 'naturel', 'traditioneel', 'origineel', 'classico',
  'mager', 'magere', 'halfvolle', 'volle', 'vol', 'light',
  'ongezouten', 'gezouten', 'gesneden', 'geraspt', 'geraspte', 'fijn', 'fijne', 'grof', 'grove',
  'wit', 'witte', 'bruin', 'bruine', 'rood', 'rode', 'geel', 'gele', 'groen', 'groene',
  'klein', 'kleine', 'groot', 'grote', 'mild', 'milde', 'scharrel', 'vrije', 'uitloop',
])

const PLURAL_SUFFIXES = ['tjes', 'jes', 'tje', 'en', 's', 'n']

/** Crude Dutch singular/plural stem, enough to tie "ui" to "uien". */
const stem = (word: string): string => {
  for (const suffix of PLURAL_SUFFIXES) {
    if (word.endsWith(suffix) && word.length - suffix.length >= 2) {
      return word.slice(0, -suffix.length)
    }
  }

  return word
}

const tokenize = (value: string): string[] =>
  value
    .toLowerCase()
    .split(/[^a-z0-9àáâäèéêëìíîïòóôöùúûüç]+/i)
    .filter(Boolean)

const isMultipack = (product: AHProduct): boolean => {
  if (/\d+\s*-?\s*pack/i.test(product.title)) {
    return true
  }

  const unitMatch = product.unitSize?.match(/^(\d+)\s*stuks/i)
  return unitMatch ? Number.parseInt(unitMatch[1], 10) > 1 : false
}

const scoreToken = (
  queryToken: string,
  titleTokens: string[]
): { score: number; matchedToken: string | null } => {
  let best = SCORE_MISSING
  let matchedToken: string | null = null

  for (const titleToken of titleTokens) {
    let score = SCORE_MISSING

    if (titleToken === queryToken) {
      score = SCORE_EXACT
    } else if (stem(titleToken) === stem(queryToken)) {
      score = SCORE_SAME_STEM
    } else if (titleToken.endsWith(queryToken)) {
      // The more the compound adds, the further it drifts from the plain
      // ingredient: roomboter over knoflookboter.
      const added = titleToken.length - queryToken.length
      score = SCORE_COMPOUND_HEAD - Math.min(1, added / PENALTY_COMPOUND_LENGTH)
    } else if (titleToken.startsWith(queryToken)) {
      score = SCORE_COMPOUND_PREFIX
    } else if (titleToken.includes(queryToken)) {
      score = SCORE_ELSEWHERE
    }

    if (score > best) {
      best = score
      matchedToken = score > 0 ? titleToken : null
    }
  }

  return { score: best, matchedToken }
}

export const scoreAHProduct = (query: string, product: AHProduct): number => {
  const queryTokens = tokenize(query)
  const titleTokens = tokenize(product.title)

  if (!queryTokens.length || !titleTokens.length) {
    return SCORE_MISSING
  }

  const matchedTitleTokens = new Set<string>()
  let score = 0

  for (const queryToken of queryTokens) {
    const { score: tokenScore, matchedToken } = scoreToken(queryToken, titleTokens)
    score += tokenScore
    if (matchedToken) {
      matchedTitleTokens.add(matchedToken)
    }
  }

  // Words the product carries beyond the ingredient and its qualifiers signal a
  // different product: a ready-made mix, a flavour variant or an accessory.
  const extraTokens = titleTokens.filter(
    (token) => !matchedTitleTokens.has(token) && !QUALIFIER_TOKENS.has(token)
  ).length
  score -= extraTokens * PENALTY_EXTRA_TOKEN

  if (isMultipack(product)) {
    score -= PENALTY_MULTIPACK
  }

  if (titleTokens.some((token) => NON_FOOD_TOKENS.has(token))) {
    score -= PENALTY_NON_FOOD
  }

  if (!product.available) {
    score -= PENALTY_UNAVAILABLE
  }

  if (typeof product.price === 'number') {
    score -= product.price * PRICE_TIEBREAK
  }

  return score
}

/**
 * Things practically everyone has in the cupboard or the tap. Matching these
 * puts five litres of mineral water or a salt-and-pepper fish fillet in the
 * cart, so they start out unchecked instead.
 */
const PANTRY_STAPLES = [
  'water',
  'zout',
  'peper',
  'zout en peper',
  'peper en zout',
  'zwarte peper',
  'versgemalen peper',
  'ijs',
  'ijsblokjes',
]

export const isPantryStaple = (name: string): boolean => {
  const normalized = name.toLowerCase().trim()
  if (PANTRY_STAPLES.includes(normalized)) {
    return true
  }

  const tokens = tokenize(normalized).filter((token) => token !== 'en')
  return tokens.length > 0 && tokens.every((token) => PANTRY_STAPLES.includes(token))
}

/** Best match first. Ties keep the order Albert Heijn returned. */
export const rankAHProducts = (query: string, products: AHProduct[]): AHProduct[] =>
  products
    .map((product, index) => ({ product, index, score: scoreAHProduct(query, product) }))
    .sort((a, b) => (b.score === a.score ? a.index - b.index : b.score - a.score))
    .map((entry) => entry.product)
